import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Target, Users, DollarSign, Award } from "lucide-react";

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: "Our Mission",
      description: "To provide sports teams and organizations with high-quality, customizable backpacks that enhance team unity and brand visibility."
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: "Community Focus",
      description: "We work closely with sports clubs, schools, and organizations to understand their unique needs and deliver products that exceed expectations."
    },
    {
      icon: <DollarSign className="w-10 h-10" />,
      title: "Affordability",
      description: "We believe quality custom backpacks should be accessible to teams of all sizes. Our competitive pricing and flexible minimum orders ensure every team can afford professional-grade custom gear."
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: "Quality Promise",
      description: "Every backpack undergoes rigorous quality testing to ensure it meets our high standards for durability, functionality, and style."
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gray-50 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              About Apa Custom
            </h1>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              We are a dedicated team passionate about creating custom backpacks that empower athletes and teams to perform at their best while showcasing their unique identity.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white" data-testid="story-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Our Story
            </h2>
            <div className="space-y-6 text-gray-700 text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p>
                Apa Custom was founded with a simple yet powerful vision: every sport should have access to custom backpacks that suit their specific needs. We recognized that athletes and teams needed more than just generic bags – they needed durable, functional, and personalized gear that reflects their identity and supports their performance.
              </p>
              <p>
                What started as a small project to help a local sports club has grown into a trusted partner for teams and organizations worldwide. Our commitment to quality, affordability, and customization has made us the go-to choice for sports clubs looking to elevate their brand and provide their athletes with the best equipment.
              </p>
              <p>
                Today, we work with basketball teams, soccer clubs, volleyball organizations, MMA gyms and countless other sports establishments to design and manufacture backpacks that are as unique as the teams that use them. Each backpack we create is a testament to our dedication to excellence and our passion for supporting the sports community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 bg-gray-50" data-testid="values-section">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`value-${index}`}
              >
                <div className="text-gray-900 mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-gray-700 text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900" data-testid="about-cta">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ready to Create Your Custom Backpacks?
          </h2>
          <p className="text-gray-300 mb-8 text-base sm:text-lg max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Let's work together to design backpacks that perfectly represent your team and meet your specific needs.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            size="lg"
            className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-6 text-lg rounded-full transition-all duration-300"
            data-testid="contact-us-btn"
          >
            Contact Us
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;